import LegalLayout, { LegalSection, LegalSubSection } from '@/components/LegalLayout';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/SeoHead';

export default function TermsOfService() {
  return (
    <>
      <SeoHead
        title="Terms of Service"
        description="Read the terms and conditions governing your use of WriteAssess."
        canonical="/terms"
      />
      <LegalLayout title="Terms of Service" lastUpdated="26 October 2026">
      <LegalSection title="1. Introduction">
        <p>
          These Terms of Service ("Terms") govern your access to and use of WriteAssess ("Service"), operated by
          WriteAssess ("we", "us", "our"). By creating an account or using the Service, you agree to be bound by
          these Terms. If you are using the Service on behalf of a school or organisation, you confirm that you have
          authority to bind that organisation to these Terms.
        </p>
        <p>
          The Service is intended for use by qualified teachers and school administrators in primary schools in England. It
          must not be accessed directly by pupils or parents.
        </p>
      </LegalSection>

      <LegalSection title="2. Account Registration">
        <p>
          To use the Service you must create an account with a valid email address and a secure password. You are
          responsible for maintaining the confidentiality of your credentials and for all activity that occurs under
          your account. You must notify us immediately at{' '}
          <Link to="/contact" className="text-primary hover:underline">our contact form</Link>{' '}
          if you suspect unauthorised access.
        </p>
        <p>
          Accounts are for individual named users. Sharing login credentials with other staff is not permitted.
          Schools requiring multiple accounts should contact us for a school plan.
        </p>
      </LegalSection>

      <LegalSection title="3. Subscription Plans and Payment">
        <LegalSubSection title="3.1 Plans">
          <p>
            We offer Free, Individual, and School subscription plans as detailed on our{' '}
            <Link to="/pricing" className="text-primary hover:underline">
              Pricing page
            </Link>
            . Features and assessment limits vary by plan. We reserve the right to modify plan features with
            reasonable notice.
          </p>
        </LegalSubSection>
        <LegalSubSection title="3.2 Billing">
          <p>
            Paid subscriptions are billed monthly or annually via Stripe. By providing payment details you authorise
            us to charge the applicable fees. WriteAssess is not currently VAT registered, so VAT is not charged.
            Prices shown are the total amount payable. This may be reviewed if VAT registration becomes required.
          </p>
        </LegalSubSection>
        <LegalSubSection title="3.3 Cancellation and Refunds">
          <p>
            You may cancel your subscription at any time via your account settings. Cancellation takes effect at the
            end of the current billing period. We do not offer pro-rata refunds for unused time within a billing
            period. School plans cancelled within 14 days of initial purchase may be eligible for a full refund at
            our discretion.
          </p>
        </LegalSubSection>
        <LegalSubSection title="3.4 Invoice Payment">
          <p>
            Schools may request payment by invoice. Invoice payment terms are 30 days from the date of invoice.
            Access is granted on a trust-first basis upon invoice generation. We reserve the right to suspend access
            if payment is not received within the agreed terms.
          </p>
        </LegalSubSection>
      </LegalSection>

      <LegalSection title="4. Acceptable Use">
        <p>You agree to use the Service only for its intended purpose: assessing pupil writing against the National
          Curriculum in England. You must not:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Upload content that is not pupil writing samples</li>
          <li>Attempt to reverse-engineer, scrape, or systematically extract content from the Service</li>
          <li>Upload images containing identifiable faces or sensitive personal data beyond what is necessary</li>
          <li>Use the Service for any purpose that violates UK law or school safeguarding obligations</li>
          <li>Share, resell, or sublicense access to the Service</li>
          <li>Upload content that infringes third-party intellectual property rights</li>
        </ul>
      </LegalSection>

      <LegalSection title="5. AI-Generated Assessments">
        <p>
          WriteAssess uses artificial intelligence to analyse pupil writing and generate assessment suggestions
          ("AI Assessments"). AI Assessments are provided as a professional aid only. They do not constitute a
          definitive judgement and must be reviewed and validated by a qualified teacher before being used for any
          formal or statutory purpose.
        </p>
        <p>
          We do not warrant that AI Assessments are accurate, complete, or free from error. Teachers retain full
          professional responsibility for all assessment decisions made using the Service.
        </p>
      </LegalSection>

      <LegalSection title="6. Intellectual Property">
        <LegalSubSection title="6.1 Our IP">
          <p>
            The Service, including its software, design, and content (excluding user-uploaded content), is owned by
            WriteAssess and protected by copyright and other intellectual property laws. You are granted a limited,
            non-exclusive, non-transferable licence to use the Service for its intended purpose during your
            subscription.
          </p>
        </LegalSubSection>
        <LegalSubSection title="6.2 Your Content">
          <p>
            You retain ownership of all pupil writing samples and data you upload. You grant us a limited licence to
            process that content solely to provide the Service to you. We do not use uploaded pupil content to train
            AI models.
          </p>
        </LegalSubSection>
      </LegalSection>

      <LegalSection title="7. Data Protection">
        <p>
          Our collection and use of personal data is described in our{' '}
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link> and{' '}
          <Link to="/data-protection" className="text-primary hover:underline">Data Protection Policy</Link>. By
          using the Service you confirm that you have appropriate legal basis to share pupil data with us and have
          informed relevant stakeholders as required by UK GDPR and your school's data protection policy.
        </p>
      </LegalSection>

      <LegalSection title="8. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, WriteAssess shall not be liable for any indirect, incidental,
          special, or consequential damages arising from your use of the Service, including but not limited to loss
          of data, loss of revenue, or reliance on AI-generated assessments.
        </p>
        <p>
          Our total aggregate liability to you for any claim arising out of or in connection with these Terms shall
          not exceed the total fees paid by you in the 12 months preceding the claim.
        </p>
        <p>
          Nothing in these Terms excludes or limits our liability for death or personal injury caused by our
          negligence, fraud, or any other liability that cannot be excluded by law.
        </p>
      </LegalSection>

      <LegalSection title="9. Availability and Modifications">
        <p>
          We aim to provide a reliable service but do not guarantee uninterrupted availability. We may modify,
          suspend, or discontinue any part of the Service with reasonable notice where practicable. We reserve the
          right to update these Terms; continued use of the Service after changes are posted constitutes acceptance
          of the revised Terms.
        </p>
      </LegalSection>

      <LegalSection title="10. Governing Law">
        <p>
          These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive
          jurisdiction of the courts of England and Wales.
        </p>
      </LegalSection>

      <LegalSection title="11. Contact">
        <p>
          For any questions about these Terms, please contact us at{' '}
          <Link to="/contact" className="text-primary hover:underline">our contact form</Link>.
        </p>
      </LegalSection>
    </LegalLayout>
    </>
  );
}
